import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPublicFieldValues,
  savePublicFieldValue,
} from "@/lib/api/public-flow/custom-field-values";
import { useSessionToken } from "@/lib/context/public-session-context";
import { publicFlowCompletionQueryKey } from "./use-public-flow-completion";

const keys = {
  fieldValues: ["publicFlow", "field-values"] as const,
};

export function usePublicFieldValues() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.fieldValues,
    queryFn: () => getPublicFieldValues(token),
    enabled: !!token,
  });
  return { fieldValues: data?.values ?? [], error, isLoading };
}

/**
 * `scopeId` (one per field and goal/portfolio) runs that field's saves one
 * after another, so a later edit can never be overwritten by an earlier save
 * that commits last (QL-211).
 */
export function useSavePublicFieldValue(scopeId?: string) {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  return useMutation({
    scope: scopeId ? { id: scopeId } : undefined,
    mutationFn: (data: {
      fieldId: string;
      value: string;
      goalId?: string | null;
      portfolioId?: string | null;
    }) => savePublicFieldValue(token, data),
    onSuccess: (result) => {
      if (result.success && result.flowFieldId) {
        queryClient.setQueryData(
          keys.fieldValues,
          (
            old: {
              values: Array<{
                flowFieldId: string;
                value: string;
                goalId: string | null;
                portfolioId: string | null;
              }>;
            } | null,
          ) => {
            const entry = {
              flowFieldId: result.flowFieldId!,
              value: result.value!,
              goalId: result.goalId ?? null,
              portfolioId: result.portfolioId ?? null,
            };
            if (!old) return { values: [entry] };
            const idx = old.values.findIndex(
              (v) =>
                v.flowFieldId === result.flowFieldId &&
                (v.goalId ?? null) === (result.goalId ?? null) &&
                (v.portfolioId ?? null) === (result.portfolioId ?? null),
            );
            const newValues = [...old.values];
            if (idx >= 0) {
              newValues[idx] = entry;
            } else {
              newValues.push(entry);
            }
            return { values: newValues };
          },
        );
      }
      // P5 — refresh the completion preflight so the progress bar / markers update.
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}
